<template>
    <div
        class="global_view_content lesson-view"
        :class="{ 'lesson-view--dark': dark }"
        :key="currentLesson"
    >
        <div class="lesson-view__inner">
            <!-- YouTube -->
            <div class="lesson-media" v-show="typeLessonPortal === 2">
                <div class="lesson-media__aspect">
                    <iframe
                        :key="videoLink"
                        :src="`https://www.youtube.com/embed/${videoLink}`"
                        title="YouTube video player"
                        frameborder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                        referrerpolicy="strict-origin-when-cross-origin"
                        allowfullscreen
                    />
                </div>
            </div>

            <!-- Vimeo -->
            <div class="lesson-media" v-show="typeLessonPortal === 3">
                <div class="lesson-media__aspect">
                    <iframe
                        :key="videoLink"
                        :src="`https://player.vimeo.com/video/${videoLink}`"
                        frameborder="0"
                        allow="autoplay; fullscreen; picture-in-picture"
                        allowfullscreen
                    />
                </div>
            </div>

            <!-- Questionário -->
            <div class="lesson-quiz" v-if="typeLessonPortal === 5" :key="typeLessonPortal">
                <div
                    v-show="hasReadyQuizScore"
                    class="lesson-quiz__summary"
                    aria-label="Resultado do questionário"
                >
                    <div class="lesson-quiz__stat lesson-quiz__stat--grade">
                        <p class="lesson-quiz__stat-label">Sua nota</p>
                        <p class="lesson-quiz__stat-value lesson-quiz__stat-value--xl">
                            <template v-if="hasReadyQuizScore">
                                {{ scoreData.grade ? Math.round(scoreData.grade / 10) : 0 }}
                            </template>
                            <template v-else>
                                {{ returnAnswer ? Math.round(returnAnswer.grade / 10) : 0 }}
                            </template>
                        </p>
                    </div>
                    <div class="lesson-quiz__stat lesson-quiz__stat--hits">
                        <p class="lesson-quiz__stat-label">Acertos</p>
                        <p class="lesson-quiz__stat-value">
                            <i class="pi pi-check lesson-quiz__stat-icon" aria-hidden="true"></i>
                            <template v-if="hasReadyQuizScore">
                                {{ scoreData.acertos }}
                            </template>
                            <template v-else>
                                {{ returnAnswer ? returnAnswer.acertos : 0 }}
                            </template>
                        </p>
                    </div>
                    <div class="lesson-quiz__stat lesson-quiz__stat--errors">
                        <p class="lesson-quiz__stat-label">Erros</p>
                        <p class="lesson-quiz__stat-value">
                            <i class="pi pi-times lesson-quiz__stat-icon" aria-hidden="true"></i>
                            <template v-if="hasReadyQuizScore">
                                {{ scoreData.erros }}
                            </template>
                            <template v-else>
                                {{ returnAnswer ? returnAnswer.erros : 0 }}
                            </template>
                        </p>
                    </div>
                </div>

                <div class="quiz-content">
                    <Question
                        :review="review"
                        :dataQuiz="dataQuiz"
                        :userAmswer="userAmswer"
                        @quiz-submitted="handleQuizSubmitted"
                    />
                </div>

                <div
                    v-if="hasNextLesson"
                    class="lesson-quiz__next"
                >
                    <button
                        type="button"
                        class="lesson-about__next-btn"
                        :aria-label="nextLessonAriaLabel"
                        :title="nextLessonTitle || undefined"
                        @click="goToNextLesson"
                    >
                        <span class="lesson-about__next-label">Próxima aula</span>
                        <fa icon="chevron-right" class="lesson-about__next-icon" />
                    </button>
                </div>
            </div>

            <!-- PDF -->
            <div class="lesson-pdf" v-show="typeLessonPortal === 4" :key="typeLessonPortal">
                <template v-if="pdfSource">
                    <iframe
                        v-if="isMobile"
                        class="lesson-pdf__frame"
                        :src="`https://docs.google.com/gview?embedded=true&url=${encodeURIComponent(pdfSource)}`"
                        frameborder="0"
                        allowfullscreen
                    />
                    <embed v-else class="lesson-pdf__embed" :src="pdfSource" />
                </template>
            </div>

            <!-- Videoteca -->
            <div class="lesson-media" v-show="typeLessonPortal === 1" :key="typeLessonPortal">
                <div class="lesson-media__aspect">
                    <iframe
                        :src="videoPlayer"
                        frameborder="0"
                        allow="autoplay; fullscreen"
                        allowfullscreen
                    />
                </div>
            </div>

            <!-- Estilo YouTube: player primeiro, título + descrição + metadados abaixo -->
            <section
                v-if="showLessonAbout"
                class="lesson-about"
                aria-labelledby="lesson-about-title"
            >
                <div class="lesson-about__head">
                    <h1
                        id="lesson-about-title"
                        class="lesson-about__title"
                    >
                        {{ lessonDetail.title }}
                    </h1>
                    <div
                        v-if="hasNextLesson"
                        class="lesson-about__head-next"
                    >
                        <button
                            type="button"
                            class="lesson-about__next-btn"
                            :aria-label="nextLessonAriaLabel"
                            :title="nextLessonTitle || undefined"
                            @click="goToNextLesson"
                        >
                            <span class="lesson-about__next-label">Próxima aula</span>
                            <fa icon="chevron-right" class="lesson-about__next-icon" />
                        </button>
                    </div>
                </div>
                <p
                    v-if="lessonDescriptionPlain"
                    class="lesson-about__description"
                >
                    {{ lessonDescriptionPlain }}
                </p>
                <dl class="lesson-about__meta">
                    <div
                        v-if="courseProgressRounded != null"
                        class="lesson-about__meta-row"
                    >
                        <dt>Progresso no curso</dt>
                        <dd>{{ courseProgressRounded }}%</dd>
                    </div>
                    <div
                        v-if="lessonProgressRounded != null"
                        class="lesson-about__meta-row"
                    >
                        <dt>Progresso nesta aula</dt>
                        <dd>{{ lessonProgressRounded }}%</dd>
                    </div>
                    <div
                        v-if="formattedVideoDuration"
                        class="lesson-about__meta-row"
                    >
                        <dt>Duração</dt>
                        <dd>{{ formattedVideoDuration }}</dd>
                    </div>
                    <div
                        v-if="lessonDetail.order != null"
                        class="lesson-about__meta-row"
                    >
                        <dt>Ordem</dt>
                        <dd>Aula {{ lessonDetail.order }}</dd>
                    </div>
                </dl>
            </section>
        </div>
    </div>
</template>

<script>
import PDFViewer from 'pdf-viewer-vue';
import Question from './utils/questions.vue';
import Loader from '@/components/Loader/loader.vue';
import api from '@/utils/portal/lesson.js';
import apiQuiz from '@/utils/portal/quiz';
import apiCourse from '@/utils/portal/course.js';
import playerVideo from '@/utils/storage/playerVideo';

import { mapState, mapActions } from 'vuex';

export default {
    components: {
        PDFViewer,
        Question,
        Loader,
    },
    props: {
        /** Lista de módulos com `lessons` (mesma estrutura da sidebar) — para calcular a próxima aula */
        modulesData: {
            type: Array,
            default: null,
        },
    },
    data() {
        return {
            data: null,
            dataQuiz: [],
            pdfSource: null,
            answers: [],
            review: false,
            hasReadyQuiz: false,
            hasReadyQuizScore: false,
            correctAnswerIndices: [],
            idQuiz: null,
            scoreData: [
                {
                    acertos: 0,
                    erros: 0,
                },
            ],
            videoLink: null,
            typeVideo: null,
            hasReady: false,
            heightPlayer: 500,
            videoPlayer: null,
            isMobile: false,
            returnAnswer: null,
            userAmswer: [],
        };
    },
    computed: {
        ...mapState([
            'token',
            'currentModuleOpen',
            'idLessonPortal',
            'nameCurrentLesson',
            'typeLessonPortal',
            'currentLesson',
            'currentCoursePortal',
            'dark',
        ]),
        lessonDetail() {
            const d = this.data;
            if (!d || typeof d !== 'object' || Array.isArray(d)) {
                return null;
            }
            return d;
        },
        showLessonAbout() {
            if (Number(this.typeLessonPortal) === 5) {
                return false;
            }
            return this.lessonDetail != null;
        },
        lessonDescriptionPlain() {
            const raw = this.lessonDetail?.description;
            if (raw == null || String(raw).trim() === '') {
                return '';
            }
            return String(raw)
                .replace(/<[^>]*>/g, ' ')
                .replace(/\r\n/g, '\n')
                .replace(/[ \t]+\n/g, '\n')
                .replace(/\n{3,}/g, '\n\n')
                .trim();
        },
        courseProgressRounded() {
            const p = this.lessonDetail?.progressCourse;
            if (p == null) {
                return null;
            }
            const n = Number(p);
            if (Number.isNaN(n)) {
                return null;
            }
            return Math.min(100, Math.max(0, Math.round(n)));
        },
        lessonProgressRounded() {
            const p = this.lessonDetail?.progress;
            if (p == null) {
                return null;
            }
            const n = Number(p);
            if (Number.isNaN(n)) {
                return null;
            }
            if (n >= 0 && n <= 1) {
                return Math.min(100, Math.max(0, Math.round(n * 100)));
            }
            return Math.min(100, Math.max(0, Math.round(n)));
        },
        formattedVideoDuration() {
            const raw = this.lessonDetail?.videoTime;
            if (raw == null || raw === '') {
                return '';
            }
            return this.formatSecondsDuration(raw);
        },
        /** Todas as aulas em ordem (módulos → aulas), com índices para o Vuex */
        flattenedLessonEntries() {
            if (!this.modulesData || !Array.isArray(this.modulesData)) {
                return [];
            }
            const out = [];
            this.modulesData.forEach((mod, moduleIndex) => {
                if (!mod || !Array.isArray(mod.lessons)) {
                    return;
                }
                mod.lessons.forEach((lesson, lessonIndex) => {
                    if (lesson && lesson.id != null) {
                        out.push({ lesson, moduleIndex, lessonIndex });
                    }
                });
            });
            return out;
        },
        nextLessonEntry() {
            const id = this.idLessonPortal;
            const flat = this.flattenedLessonEntries;
            if (flat.length === 0 || id == null) {
                return null;
            }
            const idx = flat.findIndex(
                (e) => String(e.lesson.id) === String(id)
            );
            if (idx === -1 || idx >= flat.length - 1) {
                return null;
            }
            return flat[idx + 1];
        },
        hasNextLesson() {
            return this.nextLessonEntry != null;
        },
        nextLessonTitle() {
            return this.nextLessonEntry?.lesson?.title || '';
        },
        nextLessonAriaLabel() {
            const t = this.nextLessonTitle;
            return t ? `Próxima aula: ${t}` : 'Ir para a próxima aula';
        },
    },
    methods: {
        ...mapActions([
            'storePorcetageProgressCurrentCourse',
            'storeIdLessonPortal',
            'storeCurrentLesson',
            'storeTypeLessonPortal',
            'storeCurrentModuleOpen',
            'storeNameCurrentLesson',
            'storeShowSidebar',
        ]),
        async getPlayerVideo() {
            const id = this.currentLesson.id;
            try {
                const response = await playerVideo.getPlayerVideo(this.token, id);
                this.videoPlayer = response;
                setTimeout(() => {
                    this.hasReady = true;
                }, 2000);
            } catch (error) {
                console.error('Erro ao obter o player:', error);
            }
        },
        async getDataQuiz(idQuiz) {
            try {
                const response = await apiQuiz.getDataQuizQuestion(this.token, idQuiz);
                this.dataQuiz = response;
            } catch (error) {
                console.error('Erro ao carregar o quiz:', error);
            }
        },
        async getCourseProgress() {
            try {
                if (
                    this.currentCoursePortal &&
                    this.currentCoursePortal.course &&
                    this.currentCoursePortal.course.id
                ) {
                    const courseResponse = await apiCourse.getDataCourse(
                        this.token,
                        this.currentCoursePortal.course.id
                    );
                    if (courseResponse && courseResponse.progress != null) {
                        this.storePorcetageProgressCurrentCourse({
                            porcetageProgressCurrentCourse: courseResponse.progress,
                        });
                    }
                }
            } catch (error) {
                console.error('Erro ao carregar progresso do curso:', error);
            }
        },
        async getDataLesson() {
            try {
                const response = await api.getDataLesson(
                    this.token,
                    this.currentCoursePortal.course.id,
                    this.idLessonPortal
                );

                if (response && response.progressCourse != null) {
                    this.storePorcetageProgressCurrentCourse({
                        porcetageProgressCurrentCourse: response.progressCourse,
                    });
                } else {
                    await this.getCourseProgress();
                }

                this.data = response;

                switch (response.type) {
                    case 4:
                        this.pdfSource = response.finalPath;
                        this.hasReady = true;
                        break;
                    case 5:
                        if (response.idQuiz) {
                            this.idQuiz = response.idQuiz;
                            this.hasReadyQuizScore =
                                response.listUserAnswer && response.listUserAnswer.length > 0;
                            this.answersQuiz = response.listUserAnswer || [];
                            this.userAnswersRaw = response.listUserAnswer || [];
                            this.getDataQuiz(response.idQuiz);
                            this.userAmswer = response.listUserAnswer;
                            if (this.userAmswer.length > 0) {
                                this.review = true;
                            }
                            if (this.hasReadyQuizScore) {
                                const corretos = response.listUserAnswer.filter(
                                    (item) => item.correct === 1
                                ).length;
                                const incorretos = response.listUserAnswer.filter(
                                    (item) => item.correct === 0
                                ).length;
                                const nota = (corretos / (corretos + incorretos)) * 100;
                                this.scoreData = {
                                    acertos: corretos,
                                    erros: incorretos,
                                    grade: nota,
                                };
                            }
                        }
                        break;
                    case 2:
                        this.typeVideo = 'youtube';
                        this.videoLink = this.getEmbeddedLink(response.path);
                        this.hasReady = true;
                        break;
                    case 3:
                        this.typeVideo = 'vimeo';
                        this.videoLink = this.getVimeoVideoId(response.path);
                        this.hasReady = true;
                        break;
                    case 1:
                        this.getPlayerVideo();
                        break;
                    default:
                        console.warn('Tipo de lição desconhecido:', response.type);
                }
            } catch (error) {
                console.error('Erro ao carregar a lesson:', error);
            }
        },
        storeAnswer(answer, questionIndex) {
            this.answers[questionIndex] = answer;
        },
        getEmbeddedLink(link) {
            if (link.includes('youtu.be')) {
                return link.split('/').pop().split('?')[0];
            } else if (link.includes('youtube.com/watch')) {
                const params = new URLSearchParams(new URL(link).search);
                return params.get('v');
            } else if (link.includes('youtube.com/embed/')) {
                return link.split('/embed/')[1].split('?')[0];
            } else {
                alert('Por favor, insira um link válido do YouTube.');
                return null;
            }
        },
        getVimeoVideoId(link) {
            const videoIdMatch = link.match(/vimeo\.com\/(\d+)/);
            return videoIdMatch ? videoIdMatch[1] : null;
        },
        detectMobile() {
            this.isMobile =
                /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
                    navigator.userAgent
                );
        },
        async handleQuizSubmitted() {
            await this.getDataLesson();
        },
        formatSecondsDuration(value) {
            const sec = Number(value);
            if (Number.isNaN(sec) || sec < 0) {
                return '';
            }
            if (sec === 0) {
                return '0 min';
            }
            const h = Math.floor(sec / 3600);
            const m = Math.floor((sec % 3600) / 60);
            const parts = [];
            if (h > 0) {
                parts.push(`${h}h`);
            }
            if (m > 0 || h === 0) {
                parts.push(`${m} min`);
            }
            return parts.join(' ');
        },
        /** Mesma sequência que `setCurrentLesson` na sidebar (inclui troca de módulo). */
        goToNextLesson() {
            const n = this.nextLessonEntry;
            if (!n) {
                return;
            }
            const { lesson, moduleIndex, lessonIndex } = n;
            this.storeIdLessonPortal({ idLessonPortal: lesson.id });
            this.storeCurrentLesson({ currentLesson: lessonIndex });
            this.storeTypeLessonPortal({ typeLessonPortal: lesson.type });
            this.storeCurrentModuleOpen({ currentModuleOpen: moduleIndex });
            this.storeNameCurrentLesson({ nameCurrentLesson: lesson.title });
            this.storeCurrentLesson({ currentLesson: lesson });
            if (typeof window !== 'undefined' && window.innerWidth < 768) {
                this.storeShowSidebar({ showSidebar: false });
            }
        },
    },
    created() {
        this.detectMobile();
        this.getDataLesson();
    },
    watch: {
        idLessonPortal() {
            this.hasReady = false;
            this.review = false;
            this.getDataLesson();
        },
        typeLessonPortal() {
            this.videoLink = null;
            this.typeVideo = null;
        },
        currentCoursePortal(newValue, oldValue) {
            if (oldValue && newValue && oldValue.course?.id !== newValue.course?.id) {
                this.review = false;
                this.hasReady = false;
                this.hasReadyQuizScore = false;
                this.userAmswer = [];
                this.dataQuiz = [];
                this.scoreData = [
                    {
                        acertos: 0,
                        erros: 0,
                    },
                ];
            }
        },
    },
};
</script>

<style scoped>
.lesson-view {
    --lv-bg: #ffffff;
    --lv-border: rgba(15, 23, 42, 0.08);
    --lv-text: #0f172a;
    --lv-muted: #64748b;
    flex: 1;
    min-width: 0;
    min-height: 0;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    overflow: auto;
}

.lesson-view--dark {
    --lv-bg: #14131a;
    --lv-border: rgba(255, 255, 255, 0.08);
    --lv-text: #f1f5f9;
    --lv-muted: #94a3b8;
}

.lesson-view__inner {
    flex: 1 1 auto;
    width: 100%;
    min-width: 0;
    padding: 16px;
    box-sizing: border-box;
}

.lesson-about {
    margin-top: 12px;
    padding: 0;
    text-align: left;
}

.lesson-about__head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 8px;
}

.lesson-about__head-next {
    flex-shrink: 0;
    margin-top: 2px;
}

.lesson-about__title {
    margin: 0;
    flex: 1;
    min-width: 0;
    font-size: 1.25rem;
    font-weight: 600;
    line-height: 1.3;
    letter-spacing: -0.02em;
    color: var(--lv-text);
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
}

.lesson-about__description {
    margin: 0 0 16px;
    font-size: 0.875rem;
    line-height: 1.5;
    color: var(--lv-muted);
    white-space: pre-line;
}

.lesson-about__meta {
    margin: 0;
    padding: 12px 0 0;
    border-top: 1px solid var(--lv-border);
    display: grid;
    gap: 10px;
}

.lesson-about__meta-row {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    justify-content: space-between;
    gap: 8px 16px;
}

.lesson-about__meta dt {
    margin: 0;
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--lv-muted);
}

.lesson-about__meta dd {
    margin: 0;
    font-size: 14px;
    font-weight: 600;
    color: var(--lv-text);
    font-variant-numeric: tabular-nums;
}

.lesson-about__next-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 12px 20px;
    border: 1px solid rgba(15, 23, 42, 0.12);
    border-radius: 999px;
    font-size: 14px;
    font-weight: 600;
    letter-spacing: 0.02em;
    cursor: pointer;
    color: #0f172a;
    background: #ffffff;
    box-shadow: 0 2px 10px rgba(15, 23, 42, 0.08);
    transition: transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
}

.lesson-about__next-btn:hover {
    transform: translateY(-1px);
    background: #f8fafc;
    box-shadow: 0 4px 14px rgba(15, 23, 42, 0.12);
}

.lesson-about__next-btn:focus-visible {
    outline: 2px solid #0d9488;
    outline-offset: 2px;
}

.lesson-about__next-icon {
    width: 14px !important;
    height: 14px !important;
}

.lesson-view--dark .lesson-about__next-btn {
    color: #0f172a;
    background: #ffffff;
    border-color: rgba(255, 255, 255, 0.2);
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.25);
}

.lesson-view--dark .lesson-about__next-btn:hover {
    background: #f1f5f9;
}

.lesson-view--dark .lesson-about__next-btn:focus-visible {
    outline-color: #5eead4;
}

@media (max-width: 600px) {
    .lesson-about__head {
        gap: 8px;
    }

    .lesson-about__next-btn {
        padding: 10px 14px;
        font-size: 13px;
    }
}

/* Vídeo 16:9 */
.lesson-media {
    width: 100%;
    margin-bottom: 8px;
}

.lesson-media__aspect {
    position: relative;
    width: 100%;
    aspect-ratio: 16 / 9;
    background: #0a0a0b;
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 8px 32px rgba(15, 23, 42, 0.12);
}

.lesson-view--dark .lesson-media__aspect {
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.45);
}

.lesson-media__aspect iframe {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    border: 0;
}

/* PDF */
.lesson-pdf {
    width: 100%;
    min-height: min(75vh, 720px);
    border-radius: 12px;
    overflow: hidden;
    border: 1px solid var(--lv-border);
    background: var(--lv-bg);
}

.lesson-pdf__frame,
.lesson-pdf__embed {
    display: block;
    width: 100%;
    height: min(75vh, 720px);
    border: none;
}

/* Quiz — resumo em grid, cartões leves */
.lesson-quiz {
    width: 100%;
}

.quiz-content {
    padding: 0;
    margin-top: 4px;
}

.lesson-quiz__summary {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 12px;
    width: 100%;
    max-width: 800px;
    margin: 0 auto 24px;
}

.lesson-quiz__stat {
    padding: 16px 12px;
    border-radius: 12px;
    text-align: center;
    border: 1px solid var(--lv-border);
    background: var(--lv-bg);
}

.lesson-quiz__stat-label {
    margin: 0 0 8px;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: var(--lv-muted);
}

.lesson-quiz__stat-value {
    margin: 0;
    font-size: 24px;
    font-weight: 700;
    line-height: 1.2;
    color: var(--lv-text);
    font-variant-numeric: tabular-nums;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
}

.lesson-quiz__stat-value--xl {
    font-size: 32px;
    font-weight: 800;
    letter-spacing: -0.03em;
}

.lesson-quiz__stat-icon {
    font-size: 1.05rem;
    flex-shrink: 0;
}

.lesson-quiz__stat--grade {
    background: rgba(13, 148, 136, 0.07);
    border-color: rgba(13, 148, 136, 0.22);
}

.lesson-quiz__stat--grade .lesson-quiz__stat-value {
    color: #0f766e;
}

.lesson-view--dark .lesson-quiz__stat--grade {
    background: rgba(45, 212, 191, 0.09);
    border-color: rgba(45, 212, 191, 0.25);
}

.lesson-view--dark .lesson-quiz__stat--grade .lesson-quiz__stat-value {
    color: #5eead4;
}

.lesson-quiz__stat--hits {
    background: rgba(22, 163, 74, 0.07);
    border-color: rgba(22, 163, 74, 0.2);
}

.lesson-quiz__stat--hits .lesson-quiz__stat-icon {
    color: #16a34a;
}

.lesson-quiz__stat--hits .lesson-quiz__stat-value {
    color: #166534;
}

.lesson-view--dark .lesson-quiz__stat--hits {
    background: rgba(74, 222, 128, 0.09);
    border-color: rgba(74, 222, 128, 0.22);
}

.lesson-view--dark .lesson-quiz__stat--hits .lesson-quiz__stat-value {
    color: #86efac;
}

.lesson-quiz__stat--errors {
    background: rgba(220, 38, 38, 0.07);
    border-color: rgba(220, 38, 38, 0.2);
}

.lesson-quiz__stat--errors .lesson-quiz__stat-icon {
    color: #dc2626;
}

.lesson-quiz__stat--errors .lesson-quiz__stat-value {
    color: #991b1b;
}

.lesson-view--dark .lesson-quiz__stat--errors {
    background: rgba(248, 113, 113, 0.09);
    border-color: rgba(248, 113, 113, 0.22);
}

.lesson-view--dark .lesson-quiz__stat--errors .lesson-quiz__stat-value {
    color: #fecaca;
}

.lesson-quiz__next {
    margin-top: 20px;
    padding-top: 16px;
    border-top: 1px solid var(--lv-border);
    display: flex;
    justify-content: flex-end;
}

button.end_quiz {
    background: #3b2d70;
    padding: 5px 40px;
    color: white;
    border-radius: 5px;
}

.option_question_quiz p {
    margin: 0;
}

.question-block {
    border: none;
    margin: 0;
    border-radius: 0;
}

.lesson-quiz :deep(.box_question_quiz) {
    border-radius: 12px;
    border: 1px solid var(--lv-border);
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
}

.lesson-view--dark .lesson-quiz :deep(.box_question_quiz) {
    background-color: var(--lv-bg);
    color: var(--lv-text);
    box-shadow: none;
    border-color: var(--lv-border);
}

.lesson-view--dark .lesson-quiz :deep(.number_question_quiz) {
    color: #94a3b8;
    border-bottom: 1px solid var(--lv-border);
    margin-bottom: 0;
    padding-bottom: 14px;
}

.lesson-view--dark .lesson-quiz :deep(.title_question_quiz),
.lesson-view--dark .lesson-quiz :deep(.title_question_quiz p),
.lesson-view--dark .lesson-quiz :deep(.title_question_quiz span) {
    color: var(--lv-text);
}

.lesson-view--dark .lesson-quiz :deep(.option_question_quiz:hover) {
    background-color: rgba(255, 255, 255, 0.05);
}

.lesson-view--dark .lesson-quiz :deep(span.option_input) {
    background-color: #1e293b;
    border-color: #64748b;
    color: #f1f5f9;
}

.lesson-view--dark .lesson-quiz :deep(span.selected.option_input) {
    background-color: #0d9488;
    border-color: #5eead4;
    color: #ffffff;
}

.lesson-view--dark .lesson-quiz :deep(.box_question_quiz.review span.correct.option_input) {
    background-color: #166534;
    border-color: #4ade80;
    color: #ffffff;
}

.lesson-view--dark .lesson-quiz :deep(span.selected.option_input.incorrect),
.lesson-view--dark .lesson-quiz :deep(span.option_input.incorrect) {
    background-color: #991b1b;
    border-color: #f87171;
    color: #ffffff;
}

.lesson-view--dark .lesson-quiz :deep(.option_question_quiz label span:last-child) {
    color: #cbd5e1;
}

.lesson-view--dark .lesson-quiz :deep(.option_question_quiz label span.selected:last-child) {
    color: #f8fafc;
}

.lesson-view--dark .lesson-quiz :deep(i.pi.pi-check) {
    color: #4ade80;
}

.lesson-view--dark .lesson-quiz :deep(i.pi.pi-times) {
    color: #f87171;
}

.lesson-view--dark .lesson-quiz :deep(.enviar-quiz-container) {
    border-top: 1px solid var(--lv-border);
}

.lesson-view--dark .lesson-quiz :deep(.btn-enviar-quiz:disabled) {
    background: #334155;
    color: #94a3b8;
    border: none;
}

@media (max-width: 768px) {
    .lesson-view__inner {
        padding: 12px;
    }
    .option_question_quiz {
        display: flex;
        align-items: center;
        padding: 15px 10px;
    }
    .lesson-quiz__summary {
        grid-template-columns: 1fr;
        gap: 10px;
        margin-bottom: 20px;
    }

    .lesson-quiz__stat-value--xl {
        font-size: 28px;
    }

    .lesson-quiz__stat-value {
        font-size: 22px;
    }
    .lesson-pdf__frame,
    .lesson-pdf__embed {
        height: 70vh;
    }
}

@media (max-width: 480px) {
    .lesson-pdf__frame,
    .lesson-pdf__embed {
        height: 55vh;
    }
}
</style>
